import React, { useEffect, useState } from "react";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/Components/ui/card";
import {
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableHeader,
   TableRow,
} from "@/Components/ui/table";
import { Separator } from "@/Components/ui/separator";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useToast } from "@/hooks/use-toast";
import { ImageType, ProductCategory } from "@/types/productTypes";
import { ImageIcon } from "lucide-react";
import ImagePickerDialog from "@/Components/ImagePicker/ImagePickerDialog";
import { useAdminStore } from "@/store/useAdminStore";

// Define the drag item type
const CATEGORY_TYPE = "category";

interface CategoryItemProps {
   category: ProductCategory;
   index: number;
   moveCategory: (dragIndex: number, hoverIndex: number) => void;
   onEdit: (category: { id: number; name: string }) => void;
   onDelete: (id: number) => void;
   onRemoveProduct: (productId: number) => void;
   onSelectImage: (categoryId: number) => void;
   productsByCategory: Record<number, any[]>;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
   category,
   index,
   moveCategory,
   onEdit,
   onDelete,
   onRemoveProduct,
   onSelectImage,
   productsByCategory,
}) => {
   const [editingCategory, setEditingCategory] = useState<{
      id: number;
      name: string;
   } | null>(null);

   const [{ isDragging }, drag] = useDrag({
      type: CATEGORY_TYPE,
      item: { index },
      collect: (monitor) => ({
         isDragging: monitor.isDragging(),
      }),
   });

   const [, drop] = useDrop({
      accept: CATEGORY_TYPE,
      hover: (item: { index: number }, _) => {
         if (!drag) {
            return;
         }
         const dragIndex = item.index;
         const hoverIndex = index;

         // Don't replace items with themselves
         if (dragIndex === hoverIndex) {
            return;
         }

         moveCategory(dragIndex, hoverIndex);

         // Update the index for the dragged item
         item.index = hoverIndex;
      },
   });

   return (
      <div
         ref={(node) => drag(drop(node))}
         style={{ opacity: isDragging ? 0.5 : 1 }}
         className="border rounded-md p-4 mb-4"
      >
         {editingCategory && editingCategory.id === category.id ? (
            <div className="flex space-x-2 mb-2">
               <Input
                  value={editingCategory.name}
                  onChange={(e) =>
                     setEditingCategory({
                        ...editingCategory,
                        name: e.target.value,
                     })
                  }
               />
               <Button
                  onClick={() => {
                     onEdit(editingCategory);
                     setEditingCategory(null);
                  }}
               >
                  Save
               </Button>
               <Button
                  variant="outline"
                  onClick={() => setEditingCategory(null)}
               >
                  Cancel
               </Button>
            </div>
         ) : (
            <div className="flex justify-between items-center mb-2">
               <h3 className="text-lg font-medium">{category.name}</h3>
               <div className="space-x-2">
                  <Button
                     variant="outline"
                     size="sm"
                     onClick={() =>
                        setEditingCategory({
                           id: category.id,
                           name: category.name,
                        })
                     }
                  >
                     Edit
                  </Button>
                  <Button
                     variant="destructive"
                     size="sm"
                     onClick={() => onDelete(category.id)}
                  >
                     Delete
                  </Button>
               </div>
            </div>
         )}

         {/* Category Image Section - Add this block */}
         <div className="mb-4">
            <div className="flex items-center space-x-4">
               <div className="w-24 h-24 border rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                  {category.image ? (
                     <img
                        src={category.image.url}
                        alt={category.name}
                        className="w-full h-full object-cover"
                     />
                  ) : (
                     <ImageIcon className="h-8 w-8 text-gray-400" />
                  )}
               </div>
               <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectImage(category.id)}
               >
                  {category.image ? "Change Image" : "Select Image"}
               </Button>
            </div>
         </div>

         <Separator className="my-2" />

         <div>
            <h4 className="font-medium mb-2">Products in this category:</h4>
            {productsByCategory[category.id]?.length > 0 ? (
               <ul className="space-y-1">
                  {productsByCategory[category.id].map((product) => (
                     <li
                        key={product.id}
                        className="flex justify-between items-center p-2 bg-muted rounded-sm"
                     >
                        <span>{product.name}</span>
                        <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => onRemoveProduct(product.id)}
                        >
                           Remove
                        </Button>
                     </li>
                  ))}
               </ul>
            ) : (
               <p className="text-muted-foreground">
                  No products in this category
               </p>
            )}
         </div>
      </div>
   );
};

const CategoryEditor: React.FC = () => {
   const { toast } = useToast();
   const [newCategoryName, setNewCategoryName] = useState("");
   const [_, setEditingCategory] = useState<{
      id: number;
      name: string;
   } | null>(null);
   const [currentCategoryId, setCurrentCategoryId] = useState<number | null>(
      null
   );
   const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
   const {
      categories,
      fetchCategories,
      createCategory,
      updateCategory,
      deleteCategory,
      reorderCategories,
      assignProduct,
      removeProduct,
      assignImageToCategory,
      allProducts,
      fetchProducts,
   } = useAdminStore();

   useEffect(() => {
      fetchCategories();
      if (!allProducts.length) {
         fetchProducts();
      }
   }, []);

   const handleCreateCategory = async () => {
      if (!newCategoryName.trim()) {
         toast({
            title: "Error",
            description: "Category name cannot be empty",
            variant: "destructive",
         });
         return;
      }

      try {
         await createCategory({ name: newCategoryName });
         setNewCategoryName("");
         toast({
            title: "Success",
            description: "Category created successfully",
         });
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to create category",
            variant: "destructive",
         });
      }
   };

   const handleUpdateCategory = async (category: {
      id: number;
      name: string;
   }) => {
      if (!category.name.trim()) {
         toast({
            title: "Error",
            description: "Category name cannot be empty",
            variant: "destructive",
         });
         return;
      }

      try {
         await updateCategory(category.id, { name: category.name });
         setEditingCategory(null);
         toast({
            title: "Success",
            description: "Category updated successfully",
         });
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to update category",
            variant: "destructive",
         });
      }
   };

   const handleDeleteCategory = async (id: number) => {
      if (
         !confirm(
            "Are you sure you want to delete this category? Products will be uncategorized."
         )
      ) {
         return;
      }

      try {
         await deleteCategory(id);
         toast({
            title: "Success",
            description: "Category deleted successfully",
         });
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to delete category",
            variant: "destructive",
         });
      }
   };

   const moveCategory = async (dragIndex: number, hoverIndex: number) => {
      if (dragIndex === hoverIndex) return;

      const category1 = categories[dragIndex];
      const category2 = categories[hoverIndex];

      try {
         await reorderCategories(category1.id, category2.id);
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to reorder categories",
            variant: "destructive",
         });
      }
   };

   const handleAssignProduct = async (
      productId: number,
      categoryId: number
   ) => {
      try {
         await assignProduct(productId, categoryId);
         toast({
            title: "Success",
            description: "Product assigned to category successfully",
         });
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to assign product to category",
            variant: "destructive",
         });
      }
   };

   const handleRemoveProduct = async (productId: number) => {
      try {
         await removeProduct(productId);
         toast({
            title: "Success",
            description: "Product removed from category successfully",
         });
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to remove product from category",
            variant: "destructive",
         });
      }
   };

   const handleSelectImage = (categoryId: number) => {
      setCurrentCategoryId(categoryId);
      setIsImagePickerOpen(true);
   };

   const handleImageSelect = async (image: ImageType) => {
      if (!currentCategoryId) return;

      try {
         await assignImageToCategory(currentCategoryId, image.id);
         setIsImagePickerOpen(false);
         toast({
            title: "Success",
            description: "Image assigned to category successfully",
         });
      } catch (error) {
         toast({
            title: "Error",
            description: "Failed to assign image to category",
            variant: "destructive",
         });
      }
   };

   const productsByCategory: Record<number, typeof allProducts> = {};
   const uncategorizedProducts = allProducts.filter(
      (product) => !product.productCategoryId
   );

   allProducts.forEach((product) => {
      if (product.productCategoryId) {
         if (!productsByCategory[product.productCategoryId]) {
            productsByCategory[product.productCategoryId] = [];
         }
         productsByCategory[product.productCategoryId].push(product);
      }
   });
   console.log("categories", categories);

   return (
      <DndProvider backend={HTML5Backend}>
         <div className="space-y-6">
            <Card>
               <CardHeader>
                  <CardTitle>Create New Category</CardTitle>
               </CardHeader>
               <CardContent>
                  <div className="flex space-x-2">
                     <Input
                        placeholder="Category name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                     />
                     <Button onClick={handleCreateCategory}>Create</Button>
                  </div>
               </CardContent>
            </Card>

            <Card>
               <CardHeader>
                  <CardTitle>Categories</CardTitle>
               </CardHeader>
               <CardContent>
                  <div className="space-y-4">
                     {categories.length === 0 ? (
                        <p className="text-muted-foreground">
                           No categories found
                        </p>
                     ) : (
                        categories.map(
                           (category: ProductCategory, index: number) => (
                              <CategoryItem
                                 key={category.id}
                                 category={category}
                                 index={index}
                                 moveCategory={moveCategory}
                                 onEdit={handleUpdateCategory}
                                 onDelete={handleDeleteCategory}
                                 onRemoveProduct={handleRemoveProduct}
                                 onSelectImage={handleSelectImage}
                                 productsByCategory={productsByCategory}
                              />
                           )
                        )
                     )}
                  </div>
               </CardContent>
            </Card>

            <Card>
               <CardHeader>
                  <CardTitle>Uncategorized Products</CardTitle>
               </CardHeader>
               <CardContent>
                  {uncategorizedProducts.length === 0 ? (
                     <p className="text-muted-foreground">
                        No uncategorized products
                     </p>
                  ) : (
                     <Table>
                        <TableHeader>
                           <TableRow>
                              <TableHead>Product Name</TableHead>
                              <TableHead>Assign to Category</TableHead>
                           </TableRow>
                        </TableHeader>
                        <TableBody>
                           {uncategorizedProducts.map((product) => (
                              <TableRow key={product.id}>
                                 <TableCell>{product.name}</TableCell>
                                 <TableCell>
                                    <div className="flex space-x-2">
                                       <select
                                          className="border rounded p-1"
                                          onChange={(e) => {
                                             const categoryId = parseInt(
                                                e.target.value
                                             );
                                             if (categoryId && product.id) {
                                                handleAssignProduct(
                                                   product.id,
                                                   categoryId
                                                );
                                             }
                                          }}
                                          defaultValue=""
                                       >
                                          <option value="" disabled>
                                             Select category
                                          </option>
                                          {categories.map((category) => (
                                             <option
                                                key={category.id}
                                                value={category.id}
                                             >
                                                {category.name}
                                             </option>
                                          ))}
                                       </select>
                                    </div>
                                 </TableCell>
                              </TableRow>
                           ))}
                        </TableBody>
                     </Table>
                  )}
               </CardContent>
            </Card>

            <ImagePickerDialog
               open={isImagePickerOpen}
               onOpenChange={setIsImagePickerOpen}
               onImageSelect={handleImageSelect}
               dialogTitle="Select Category Image"
            />
         </div>
      </DndProvider>
   );
};

export default CategoryEditor;
